import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faPhoneAlt,
  faEnvelope,
  faUserEdit,
  faLock,
  faClipboardList,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import s from '../CaseFilePage.module.css';

interface ClientHeaderCardProps {
  headerTitle: string;
  contact: Record<string, unknown> | undefined;
  actionsMenuOpen: boolean;
  setActionsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  emailSubMenuOpen: boolean;
  setEmailSubMenuOpen: (v: boolean) => void;
  handleCallClient: () => void;
  handleEmailClient: () => void;
  handleEmailDefendant: () => void;
  handleEmailDefSol: () => void;
  handleConnectOutlook?: () => void;
  isOutlookConnected?: boolean;
  setReassignModalOpen: (v: boolean) => void;
  setViewAuditOpen: (v: boolean) => void;
  canReassign: boolean;
  canViewAudit: boolean;
  canEmail?: boolean;
  fmtUKDate: (val: unknown) => string;
}

export default function ClientHeaderCard({
  headerTitle,
  contact,
  actionsMenuOpen,
  setActionsMenuOpen,
  emailSubMenuOpen,
  setEmailSubMenuOpen,
  handleCallClient,
  handleEmailClient,
  handleEmailDefendant,
  handleEmailDefSol,
  handleConnectOutlook,
  isOutlookConnected,
  setReassignModalOpen,
  setViewAuditOpen,
  canReassign,
  canViewAudit = true,
  canEmail = true,
  fmtUKDate,
}: ClientHeaderCardProps) {
  return (
    <div className={s.clientCard}>
      <div
        className={s.clientCardMain}
        onClick={() => {
          const cid = (contact as Record<string, unknown> | undefined)?.ContactID;
          if (cid) window.open(`/contacts/${cid}`, '_blank');
        }}
        title="Click to open contact"
      >
        <h2 className={s.clientName}>{headerTitle}</h2>
        <div className={s.clientCardDivider} />
        <div className={s.clientCardRow}>
          <span>
            <strong>DOB:</strong>
            <br />
            {(() => {
              const raw = contact as Record<string, unknown>;
              const dob = raw?.['Date of Birth '] || raw?.['Date of Birth'] || raw?.date_of_birth;
              return dob ? fmtUKDate(dob as string) : '—';
            })()}
          </span>
          <span>
            <strong>Address:</strong>
            <br />
            {[
              ...(Array.isArray(contact?.Address)
                ? (contact.Address as string[])
                : contact?.Address
                  ? [String(contact.Address)]
                  : []),
              (contact?.Postcode || (contact as Record<string, unknown>)?.PostCode) as
                | string
                | undefined,
            ]
              .filter(Boolean)
              .join(', ') || '—'}
          </span>
          <span>
            <strong>Email:</strong>
            <br />
            {(contact?.Email || contact?.email || '—') as string}
          </span>
          <span>
            <strong>Phone:</strong>
            <br />
            {(contact as Record<string, string | undefined>)?.['Phone (main)'] ||
              (contact?.Phone as string) ||
              (contact?.phone as string) ||
              '—'}
          </span>
        </div>
      </div>
      {/* Contact quick-actions menu (toggle) */}
      <div
        className={`${s.contactActionsContainer} ${actionsMenuOpen ? s.contactActionsActive : ''}`}
      >
        <button
          className={`${s.contactActionBtn} ${s.menuToggle} ${actionsMenuOpen ? s.menuToggleActive : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setEmailSubMenuOpen(false);
            setActionsMenuOpen((p) => !p);
          }}
          data-tooltip="Contact Actions"
        >
          <FontAwesomeIcon icon={faEllipsisV} />
        </button>
        <button
          className={`${s.contactActionBtn} ${s.actionItem}`}
          onClick={(e) => {
            e.stopPropagation();
            handleCallClient();
            setActionsMenuOpen(false);
          }}
          data-tooltip="Call Client"
        >
          <FontAwesomeIcon icon={faPhoneAlt} />
        </button>
        <div
          role="button"
          className={`${s.contactActionBtn} ${s.actionItem}${!canEmail ? ` ${s.actionItemBlocked}` : ''}`}
          data-tooltip={emailSubMenuOpen ? undefined : canEmail ? 'Email' : 'Permission Blocked'}
          style={{
            position: 'relative',
            overflow: 'visible',
            cursor: canEmail ? 'pointer' : 'not-allowed',
          }}
          onMouseEnter={() => canEmail && setEmailSubMenuOpen(true)}
          onMouseLeave={() => setEmailSubMenuOpen(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={canEmail ? faEnvelope : faLock} />
          {canEmail && emailSubMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                paddingTop: 2,
                zIndex: 200,
              }}
            >
              <div className={s.emailSubMenu}>
                <button
                  className={s.emailSubItem}
                  onClick={() => {
                    handleEmailClient();
                    setEmailSubMenuOpen(false);
                    setActionsMenuOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  &nbsp;Email Client
                </button>
                <button
                  className={s.emailSubItem}
                  onClick={() => {
                    handleEmailDefendant();
                    setEmailSubMenuOpen(false);
                    setActionsMenuOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  &nbsp;Email Defendant
                </button>
                <button
                  className={s.emailSubItem}
                  onClick={() => {
                    handleEmailDefSol();
                    setEmailSubMenuOpen(false);
                    setActionsMenuOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  &nbsp;Email Def Sol
                </button>
                {!isOutlookConnected && (
                  <button
                    className={s.emailSubItem}
                    onClick={() => {
                      handleConnectOutlook?.();
                      setEmailSubMenuOpen(false);
                      setActionsMenuOpen(false);
                    }}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: '#60a5fa' }}
                  >
                    <FontAwesomeIcon icon={faCog} />
                    &nbsp;Connect Outlook
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <button
          className={`${s.contactActionBtn} ${s.actionItem}${!canReassign ? ` ${s.actionItemBlocked}` : ''}`}
          onClick={
            canReassign
              ? (e) => {
                  e.stopPropagation();
                  setReassignModalOpen(true);
                  setActionsMenuOpen(false);
                }
              : undefined
          }
          disabled={!canReassign}
          data-tooltip={canReassign ? 'Reassign Contact' : 'Permission Blocked'}
          style={!canReassign ? { cursor: 'not-allowed' } : undefined}
        >
          <FontAwesomeIcon icon={canReassign ? faUserEdit : faLock} />
        </button>
        <button
          className={`${s.contactActionBtn} ${s.actionItem}${!canViewAudit ? ` ${s.actionItemBlocked}` : ''}`}
          onClick={
            canViewAudit
              ? (e) => {
                  e.stopPropagation();
                  setViewAuditOpen(true);
                  setActionsMenuOpen(false);
                }
              : undefined
          }
          disabled={!canViewAudit}
          data-tooltip={canViewAudit ? 'View Audit' : 'Permission Blocked'}
          style={!canViewAudit ? { cursor: 'not-allowed' } : undefined}
        >
          <FontAwesomeIcon icon={canViewAudit ? faClipboardList : faLock} />
        </button>
      </div>
    </div>
  );
}
